import { useEffect, useRef, useState } from "react";
import { Raiz } from "./styled";
import Paginacao from "../Paginacao";

/**
 * Lista administrativa de LARGURA TOTAL.
 *
 * Com espaço, cada item vira uma linha em colunas alinhadas, com cabeçalho. Sem
 * espaço (tela estreita, ou menu aberto numa janela pequena), cada item volta a
 * ser um cartão empilhado, com o rótulo de cada coluna acima do valor.
 *
 * A decisão é pela largura do PRÓPRIO bloco (ResizeObserver), não da janela: o
 * menu lateral aberto ou recolhido muda o espaço sem mudar a janela. É JS e não
 * CSS (@container) porque o styled-components 5 não gera CSS válido para
 * @container aninhado.
 *
 * colunas: [{ id, rotulo, largura?, direita?, celula: (linha) => node }]
 *   largura: valor CSS de uma trilha do grid (ex.: "minmax(220px, 2.2fr)").
 *   direita: alinha o conteúdo à direita (coluna de ações).
 */
export default function TabelaAdmin({ rotulo, colunas, linhas, chave, larguraMinima = 900, vazio, className, paginar, itens = "itens" }) {
  const raiz = useRef(null);
  const [larga, setLarga] = useState(false);
  // Paginação NO CLIENTE, para tabelas que já recebem todas as linhas (rankings dos
  // relatórios). As listas que consultam o servidor página a página usam o próprio
  // <Paginacao> no lugar de usar isto.
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(paginar || 0);

  useEffect(() => {
    const el = raiz.current;
    if (!el) return undefined;
    const medir = () => setLarga(el.getBoundingClientRect().width >= larguraMinima);
    medir();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", medir);
      return () => window.removeEventListener("resize", medir);
    }
    const obs = new ResizeObserver(medir);
    obs.observe(el);
    return () => obs.disconnect();
  }, [larguraMinima]);

  // Mudou o conjunto de linhas (filtro novo): volta para a primeira página.
  useEffect(() => { setPagina(1); }, [linhas.length]);

  const comPaginacao = !!paginar && linhas.length > paginar;
  const visiveis = comPaginacao ? linhas.slice((pagina - 1) * porPagina, pagina * porPagina) : linhas;

  const trilhas = colunas.map((c) => c.largura || "minmax(120px, 1fr)").join(" ");

  return (
    <>
    <Raiz
      ref={raiz}
      className={`${larga ? "larga" : "estreita"} ${className || ""}`}
      style={{ "--colunas": trilhas }}
      role="table"
      aria-label={rotulo}
    >
      {linhas.length > 0 &&
        <div className="cabecalho" role="row">
          {colunas.map((c) => (
            <span key={c.id} role="columnheader" className={c.direita ? "direita" : undefined}>{c.rotulo}</span>
          ))}
        </div>
      }

      {visiveis.map((linha) => (
        <div className="linha" role="row" key={chave(linha)}>
          {colunas.map((c) => (
            <div key={c.id} role="cell" className={`celula${c.direita ? " direita" : ""}`}>
              <span className="rotulo-celula">{c.rotulo}</span>
              {c.celula(linha)}
            </div>
          ))}
        </div>
      ))}

      {linhas.length === 0 && vazio && <div className="vazio-tabela">{vazio}</div>}
    </Raiz>
    {comPaginacao &&
      <Paginacao
        pagina={pagina} total={linhas.length} porPagina={porPagina} itens={itens}
        opcoes={[paginar, paginar * 2, paginar * 4]}
        onMudarPagina={setPagina}
        onMudarPorPagina={(n) => { setPorPagina(n); setPagina(1); }}
      />
    }
    </>
  );
}
